import type { Post } from '@/@types/post'
import ActionButton, { type ActionButtonProps } from '@/components/ActionButton'
import { BlurTextMode, FeaturableBlurTextSwitch } from '@/components/BlurTextSwitch'
import DropdownMenu from '@/components/DropdownMenu/DropdownMenu'
import type { MenuItemData } from '@/components/DropdownMenu/types'
import { POST_TYPE_TO_ROUTE_MAPPING } from '@/utils/route'
import { css } from '@emotion/react'
import { RepostIcon } from '@ruguoapp/jike-fe-icons'
import { compact } from 'lodash-es'
import { type HTMLAttributes, useCallback, useMemo } from 'react'
import toast from 'react-hot-toast'
import { useRepostFormModal } from '../RepostForm/RepostFormModal'
import { ACTION_MENU_LOCALE, ActionMenu } from '../config'

export interface RepostMenuButtonProps extends HTMLAttributes<HTMLDivElement> {
	data: Post
}

export default function RepostMenuButton({ data, ...props }: RepostMenuButtonProps) {
	const { shareCount } = data
	const { open } = useRepostFormModal()
	const menuData = useMemo(
		() =>
			compact([
				data.repostable && ACTION_MENU_LOCALE[ActionMenu.REPOST],
				ACTION_MENU_LOCALE[ActionMenu.COPY_LINK],
			]) as MenuItemData[],
		[data.repostable]
	)

	const handleSelect = useCallback(
		(menuId: string) => {
			switch (menuId) {
				case ActionMenu.COPY_LINK:
					{
						const username = data?.user?.username
						if (!username) return
						const postType = POST_TYPE_TO_ROUTE_MAPPING[data.type]
						const url = new URL(`/u/${username}/${postType}/${data.id}`, window.location.origin)
						navigator.clipboard
							.writeText(url.toString())
							.then(() => {
								toast.success('已复制链接')
							})
							.catch(() => {
								toast.error('复制失败')
							})
					}
					break
				case ActionMenu.REPOST:
					open(data)
					break
			}
		},
		[data, open]
	)

	return (
		<DropdownMenu
			data={menuData}
			onSelect={handleSelect}
			position="top-start"
			renderTrigger={(props) => (
				<ActionButton
					icon={
						<RepostIcon
							width="20px"
							height="20px"
							css={css`
								transition: color 0.2s;
							`}
						/>
					}
					{...(props as ActionButtonProps)}
				>
					<FeaturableBlurTextSwitch data={(shareCount ?? 0).toString()} mode={BlurTextMode.SMALL} />
				</ActionButton>
			)}
		/>
	)
}
